// Packaging Options: saves the "Packaging Preference" cart attribute through the Cart AJAX API.
// State lives at module level, so it survives the cart drawer re-rendering its HTML.
(() => {
  if (customElements.get('packaging-options')) return;

  const KEY = 'Packaging Preference';
  const REQUEST_TIMEOUT = 10000;

  // Checkout entry points inside the cart drawer that this script holds while a save is pending
  // or has failed. Add [data-packaging-checkout] to any other drawer checkout control (for example
  // an app-injected button) to include it.
  const CHECKOUT_TRIGGERS = [
    '#CartDrawer-Checkout',
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href*="/checkout"]',
    '[data-packaging-checkout]',
  ].join(',');

  const state = {
    desired: null, // what the customer chose (or the default)
    confirmed: null, // what the server confirmed is saved on the cart
    status: 'idle', // 'idle' | 'saving' | 'error'
    flushing: null, // promise of the running save loop
  };

  // ---------- Saving ----------

  function postAttribute(value) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    return fetch(`${window.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [KEY]: value } }),
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart update failed (${response.status})`);
        return response.json();
      })
      .then((cart) => {
        // Only treat the save as done when the server returns the value we sent.
        const saved = cart && cart.attributes ? cart.attributes[KEY] : undefined;
        if (saved !== value) throw new Error(`Cart returned "${saved}" instead of "${value}"`);
      })
      .finally(() => clearTimeout(timer));
  }

  // One request at a time. If the customer switches options mid-save, the loop sends the latest
  // choice once the current request finishes, so requests never race each other.
  function flush() {
    if (state.flushing) return state.flushing;
    state.status = 'saving';
    render();

    state.flushing = (async () => {
      while (state.desired !== state.confirmed) {
        const value = state.desired;
        try {
          await postAttribute(value);
          state.confirmed = value;
        } catch (error) {
          console.error('[Packaging Options]', error);
          if (state.desired === value) {
            state.status = 'error';
            return false;
          }
          // The choice changed while this request failed: loop and save the newer choice.
        }
      }
      state.status = 'idle';
      return true;
    })().finally(() => {
      state.flushing = null;
      render();
    });

    return state.flushing;
  }

  function choose(value) {
    state.desired = value;
    return flush();
  }

  // ---------- UI ----------

  function render() {
    document.querySelectorAll('packaging-options').forEach((element) => {
      if (element.inputs) element.render();
    });

    const busy = state.status !== 'idle';
    document.querySelectorAll('cart-drawer').forEach((drawer) => {
      drawer.querySelectorAll(CHECKOUT_TRIGGERS).forEach((trigger) => {
        if (busy) {
          trigger.setAttribute('aria-disabled', 'true');
        } else {
          trigger.removeAttribute('aria-disabled');
          trigger.classList.remove('is-packaging-held');
        }
      });
    });
  }

  // ---------- Checkout protection (cart drawer only) ----------

  let releasing = false;

  function continueCheckout(event, trigger) {
    releasing = true;
    try {
      if (trigger && typeof trigger.click === 'function') {
        trigger.click();
      } else if (event.type === 'submit' && event.target.requestSubmit) {
        event.target.requestSubmit();
      }
    } finally {
      releasing = false;
    }
  }

  function guardCheckout(event) {
    if (releasing || state.status === 'idle') return;

    let trigger = null;
    if (event.type === 'click') {
      trigger = event.target.closest(CHECKOUT_TRIGGERS);
      if (!trigger || !trigger.closest('cart-drawer')) return;
    } else {
      if (event.target.id !== 'CartDrawer-Form') return;
      trigger = event.submitter || null;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (state.status === 'error') {
      focusRetry();
      return;
    }

    // Save still pending: hold this checkout and continue it once the save is confirmed.
    if (trigger) trigger.classList.add('is-packaging-held');
    flush().then((ok) => {
      if (trigger) trigger.classList.remove('is-packaging-held');
      if (ok) {
        continueCheckout(event, trigger);
      } else {
        focusRetry();
      }
    });
  }

  function focusRetry() {
    const retry = document.querySelector('packaging-options .packaging-options__retry');
    if (retry) retry.focus();
  }

  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);

  // ---------- Element ----------

  customElements.define(
    'packaging-options',
    class PackagingOptions extends HTMLElement {
      connectedCallback() {
        this.inputs = Array.from(this.querySelectorAll('.packaging-option__input'));
        this.error = this.querySelector('.packaging-options__error');
        this.onChange = this.onChange.bind(this);
        this.onRetry = this.onRetry.bind(this);
        this.addEventListener('change', this.onChange);
        this.querySelector('.packaging-options__retry')?.addEventListener('click', this.onRetry);

        const allowed = this.inputs.map((input) => input.value);
        const serverValue = this.dataset.savedValue || '';

        if (allowed.includes(serverValue)) {
          // Server HTML is the truth unless a save is running or failed in this page session.
          if (state.status === 'idle') {
            state.confirmed = serverValue;
            state.desired = serverValue;
          }
        } else {
          // Nothing (valid) saved on the cart yet: save the default, or the choice already made.
          state.confirmed = null;
          if (!state.desired) state.desired = this.dataset.defaultValue;
          flush();
        }

        this.render();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.querySelector('.packaging-options__retry')?.removeEventListener('click', this.onRetry);
      }

      onChange(event) {
        if (!event.target.matches('.packaging-option__input')) return;
        // Keep this change away from <cart-drawer-items>, which treats every change as a quantity edit.
        event.stopPropagation();
        choose(event.target.value);
      }

      onRetry() {
        flush();
      }

      render() {
        // Always show the customer's intended choice, even while saving or after a failure.
        if (state.desired) {
          this.inputs.forEach((input) => {
            input.checked = input.value === state.desired;
          });
        }
        this.setAttribute('aria-busy', state.status === 'saving' ? 'true' : 'false');
        if (this.error) this.error.hidden = state.status !== 'error';
      }
    }
  );
})();

/* Keep Effective Apps Gift Options above cart subtotal */
(function () {
  function positionGiftOptions() {
    const drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    const giftWidget = drawer.querySelector(
      'label[for="effectiveAppsEGW"]'
    );

    const footer = drawer.querySelector('.drawer__footer');

    if (!giftWidget || !footer) return;

    if (giftWidget.nextElementSibling === footer) return;

    footer.parentNode.insertBefore(giftWidget, footer);
  }

  document.addEventListener('DOMContentLoaded', positionGiftOptions);

  const observer = new MutationObserver(positionGiftOptions);

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
